import React from "react";
import { Inbox } from "lucide-react";
import PennText from "@/components/ui/text/PennText";

export type ColDef<T> = {
  header: string;
  key?: keyof T;
  render?: (row: T) => React.ReactNode;
  width?: string;
};

type AdminTableProps<T> = {
  columns: ColDef<T>[];
  rows: T[];
  keyExtractor: (row: T) => string | number;
  emptyMessage?: string;
  loading?: boolean;
};

const SkeletonRow = ({ cols }: { cols: number }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" style={{ width: `${60 + (i * 13) % 30}%` }} />
      </td>
    ))}
  </tr>
);

export function AdminTable<T>({
  columns,
  rows,
  keyExtractor,
  emptyMessage = "No records found.",
  loading = false,
}: AdminTableProps<T>) {
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-body">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800">
              {columns.map((col) => (
                <th
                  key={col.header}
                  className={`text-left px-4 py-3 text-label text-blue_grey3 whitespace-nowrap ${col.width ?? ""}`}
                >
                  {/* {toPascalCase()} */}
                 { col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={columns.length} />)
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Inbox size={36} strokeWidth={1.2} className="text-blue_grey4" />
                    <PennText as="p" size="sm" font="medium" color="blue_grey">
                      {emptyMessage}
                    </PennText>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={keyExtractor(row)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                >
                  {columns.map((col) => (
                    <td key={col.header} className="px-4 py-3 text-blue_grey text-body">
                      {col.render
                        ? col.render(row)
                        : col.key
                        ? String(row[col.key] ?? "")
                        : null}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
          <PennText as="p" size="xs" color="blue_grey">
            {rows.length} {rows.length === 1 ? "record" : "records"}
          </PennText>
        </div>
      )}
    </div>
  );
}
